import { BREAKDOWN_FIELD_MAP, W8_FORM_VALIDITY_YEARS } from '../constants/EarningsPayout';
import { formatUsdAmountDisplayCents } from './function';
import { ROLE } from '../constants/common';

export const formatDateToString = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const month = date.toLocaleString('default', { month: 'short' });
    const day = String(date.getDate()).padStart(2, '0');
    return `${month} ${day}`;
  } catch (error) {
    return dateString;
  }
};

/** Start of today at 12:00 AM in the user's local timezone. */
export const getTodayStart = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

/** Today's date as YYYY-MM-DD in local time (for display/filters). */
export const getTodayLocalDateString = () => formatDateToString(new Date());

/**
 * Returns ISO 8601 timestamp with explicit timezone offset (e.g. 2025-01-30T00:00:00.000-05:00).
 * Makes dates unambiguous for backend regardless of server timezone.
 * @param {Date} date
 * @param {boolean} endOfDay - if true, use 23:59:59.999; otherwise 00:00:00.000
 * @returns {string|null}
 */
export const formatToISOWithTimezone = (date, endOfDay = false) => {
  if (!date) return null;
  const d = new Date(date);
  if (endOfDay) {
    d.setHours(23, 59, 59, 999);
  } else {
    d.setHours(0, 0, 0, 0);
  }
  const offset = -d.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const absOffset = Math.abs(offset);
  const hours = String(Math.floor(absOffset / 60)).padStart(2, '0');
  const mins = String(absOffset % 60).padStart(2, '0');
  const offsetStr = `${sign}${hours}:${mins}`;
  const pad = (n) => String(n).padStart(2, '0');
  const ms = endOfDay ? '.999' : '.000';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}${ms}${offsetStr}`;
};

/**
 * Standard date ranges for earnings filter (all in user's local timezone).
 * Returns ISO 8601 timestamps with explicit offset for unambiguous backend parsing.
 * - Today: 12:00 AM → now (local time)
 * - Yesterday: 12:00 AM → 11:59 PM of the previous day
 * - Last N days: rolling N days (today + previous N-1 days)
 * - This month: 1st of current month → now
 * - This year: Jan 1 → Dec 31 (current year)
 * - All time: lifetime since account creation
 */
export const CALCULATE_DATE_RANGE = (period) => {
  const today = getTodayStart();

  switch (period) {
    case 'Today':
      return {
        startDate: formatToISOWithTimezone(today, false),
        endDate: formatToISOWithTimezone(today, true),
      };

    case 'Yesterday': {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return {
        startDate: formatToISOWithTimezone(yesterday, false),
        endDate: formatToISOWithTimezone(yesterday, true),
      };
    }

    case 'Last 7 days':
    case 'last 7 days': {
      const start = new Date(today);
      start.setDate(start.getDate() - 6); // today + previous 6 days = 7 days
      return {
        startDate: formatToISOWithTimezone(start, false),
        endDate: formatToISOWithTimezone(today, true),
      };
    }

    case 'Last 14 days':
    case 'last 14 days': {
      const start = new Date(today);
      start.setDate(start.getDate() - 13); // rolling 14 days
      return {
        startDate: formatToISOWithTimezone(start, false),
        endDate: formatToISOWithTimezone(today, true),
      };
    }

    case 'Last 30 days':
    case 'last 30 days': {
      const start = new Date(today);
      start.setDate(start.getDate() - 29); // rolling 30 days
      return {
        startDate: formatToISOWithTimezone(start, false),
        endDate: formatToISOWithTimezone(today, true),
      };
    }

    case 'This month': {
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        startDate: formatToISOWithTimezone(firstDayOfMonth, false),
        endDate: formatToISOWithTimezone(today, true),
      };
    }

    case 'This year': {
      const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
      const lastDayOfYear = new Date(today.getFullYear(), 11, 31);
      return {
        startDate: formatToISOWithTimezone(firstDayOfYear, false),
        endDate: formatToISOWithTimezone(lastDayOfYear, true),
      };
    }

    case 'All time':
    default:
      return {
        startDate: null,
        endDate: null
      };
  }
};

/**
 * Parses the form date from W-8 API response.
 * Accepts signatureDate (dd / MM / yyyy), createdAt, or updatedAt.
 * @param {Object} formData - W-8 form data from API
 * @returns {Date|null} Parsed date or null if not available
 */
export const parseW8FormDate = (formData) => {
  if (!formData || typeof formData !== 'object') return null;
  // Prefer backend timestamps (when form was submitted)
  const isoDate = formData.createdAt || formData.created_at || formData.updatedAt || formData.updated_at;
  if (isoDate) {
    const d = new Date(isoDate);
    return isNaN(d.getTime()) ? null : d;
  }
  // Fall back to signatureDate (dd / MM / yyyy)
  const sig = formData.signatureDate;
  if (!sig || typeof sig !== 'string') return null;
  const match = sig.trim().match(/^(\d{1,2})\s*\/\s*(\d{1,2})\s*\/\s*(\d{4})$/);
  if (!match) return null;
  const [, day, month, year] = match;
  const d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Checks if the W-8BEN form has expired (3+ years since form date).
 * @param {Object} formData - W-8 form data from API
 * @returns {boolean} True if form is expired or date cannot be determined
 */
export const isW8FormExpired = (formData) => {
  const formDate = parseW8FormDate(formData);
  if (!formDate) return true; // No date = treat as expired / needs update
  const now = new Date();
  const yearsDiff = (now - formDate) / (365.25 * 24 * 60 * 60 * 1000);
  return yearsDiff >= W8_FORM_VALIDITY_YEARS;
};

/**
 * Whether the "Add Tax Information" row should appear in Account Setup.
 * - US (W9): hidden once properly submitted (one-time requirement).
 * - Foreign (W8): hidden while a valid, signed form exists; shown when missing, expired, or awaiting signature.
 */
export const shouldShowTaxInfoInAccountSetup = ({
  isUsUser,
  w9FormFilled = false,
  w8FormFilled = false,
  w8FormExpired = false,
  w8PendingSigningUrl = null,
}) => {
  if (isUsUser) {
    return !w9FormFilled;
  }
  return !w8FormFilled || w8FormExpired || !!w8PendingSigningUrl;
};

// generate html for pdf
export const COLUMN_NAME_MAP = {
  'subscriptions': 'Subs',
  'contentPurchases': 'Content Purchases',
  'tips': 'Tips',
  'voiceVideoCalls': 'Calls',
  'messagePurchases': 'Msgs',
  'messageTips': 'Msg Tips',
  'referrals': 'Refs',
  'transactionCount': 'Total',
  'date': 'Date'
};

export const formatColumnName = (key) => {
  if (COLUMN_NAME_MAP[key]) {
    return COLUMN_NAME_MAP[key];
  }
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
};

export const COLUMN_NAME_MAP_PDF = {
  'subscriptions': 'Subs',
  'contentPurchases': 'Content Purchases',
  'tips': 'Tips',
  'voiceVideoCalls': 'Voice/Video',
  'messagePurchases': 'Msg Purch.',
  'messageTips': 'Msg Tips',
  'referrals': 'Ref',
  'transactionCount': 'Total',
  'date': 'Date'
};

export const formatColumnNamePDF = (key) => {
  if (COLUMN_NAME_MAP_PDF[key]) {
    return COLUMN_NAME_MAP_PDF[key];
  }
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
};

// Constants matching EarningsReport.js
export const COLUMN_ORDER = [
  'date',
  'subscriptions',
  'contentPurchases',
  'tips',
  'voiceVideoCalls',
  'messagePurchases',
  'messageTips',
  'referrals',
  'transactionCount'
];

// Helper functions matching EarningsReport.js
export const shouldHideColumn = (key, userRole) => {
  if (userRole === ROLE.CREATOR_CONTENT && key === 'subscriptions') {
    return true;
  }
  if (userRole === ROLE.CREATOR_SUB && key === 'contentPurchases') {
    return true;
  }
  return false;
};

export const formatCellValue = (key, value, day) => {
  if (key === 'date') {
    return formatDate(value);
  }
  if (key === 'transactionCount' && day.netEarnings !== undefined) {
    return `$${formatUsdAmountDisplayCents(day.netEarnings)}`;
  }
  if (typeof value === 'number') {
    return `$${formatUsdAmountDisplayCents(value)}`;
  }
  return value || 0;
};

export const shouldHaveZeroClass = (key, value) => {
  if (key === 'date' || key === 'transactionCount') return false;
  return value === 0 || value === null || value === undefined;
};

export const getSortedColumns = (dailyEarnings, userRole) => {
  const allKeys = new Set();
  dailyEarnings.forEach(day => {
    Object.keys(day).forEach(key => allKeys.add(key));
  });

  allKeys.delete('netEarnings');

  return Array.from(allKeys)
    .sort((a, b) => {
      const indexA = COLUMN_ORDER.indexOf(a);
      const indexB = COLUMN_ORDER.indexOf(b);

      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    })
    .filter(key => !shouldHideColumn(key, userRole));
};

// Helper function to format date for generated date display
const formatGeneratedDate = () => {
  const today = new Date();
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const month = months[today.getMonth()];
  const day = today.getDate();
  const year = today.getFullYear();
  return `${month} ${day}, ${year}`;
};

// Helper function to format date for table (e.g., "Jan 01")
const formatTableDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const month = date.toLocaleString('default', { month: 'short' });
    const day = String(date.getDate()).padStart(2, '0');
    return `${month} ${day}`;
  } catch (error) {
    return dateString;
  }
};

const renderDailyEarningsTable = (dailyEarnings, userRole) => {
  if (dailyEarnings.length === 0) {
    return `
      <table>
        <tbody>
          <tr>
            <td colspan="100%" style="text-align: center; padding: 1rem;">
              No daily earnings data available
            </td>
          </tr>
        </tbody>
      </table>
    `;
  }

  const sortedKeys = getSortedColumns(dailyEarnings, userRole);

  let tableHTML = `
    <table>
      <thead>
        <tr>
          ${sortedKeys.map((key) => `<th>${formatColumnNamePDF(key)}</th>`).join('')}
        </tr>
      </thead>
      <tbody>
  `;

  dailyEarnings.forEach((day) => {
    tableHTML += '<tr>';
    sortedKeys.forEach((key) => {
      const value = day[key];
      let cellValue;
      if (key === 'date') {
        cellValue = formatTableDate(value);
      } else if (key === 'transactionCount' && day.netEarnings !== undefined) {
        cellValue = `$${formatUsdAmountDisplayCents(day.netEarnings)}`;
      } else if (typeof value === 'number') {
        cellValue = `$${formatUsdAmountDisplayCents(value)}`;
      } else {
        cellValue = value || '$0.00';
      }
      tableHTML += `<td>${cellValue}</td>`;
    });
    tableHTML += '</tr>';
  });

  tableHTML += `
      </tbody>
    </table>
  `;

  return tableHTML;
};

// @param {Object} earningsData - Earnings data from API
// @param {string} year - Year of earnings data
// @param {Object} user - User data from API
// @param {string} taxType - Type of tax (vat or gst or none)
// @param {string} taxNumber - Tax number
export const generateHtmlForPdf = (earningsData, year, user, taxType = 'none', taxNumber = 'N/A') => {
  if (!earningsData) {
    return '<html><body><p>No data available</p></body></html>';
  }

  const userRole = user?.rol;
  const monthlyData = earningsData.motnthly || [];
  const overview = earningsData.overview || {};
  const generatedDate = formatGeneratedDate();
  const documentId = `EXP-${year}-001`;
  const period = earningsData.Summary || `January ${year} - December ${year}`;

  // Calculate total VAT remitted from all months
  const totalVatRemitted = monthlyData.reduce((sum, month) => {
    return sum + (month.vatRemitted || 0);
  }, 0);

  // Generate Summary Grid HTML
  const summaryItems = Object.entries(overview)
    .filter(([key]) => !shouldHideColumn(key, userRole) && key !== 'vatRemitted' && key !== 'netEarnings')
    .map(([key, value]) => {
      const label = BREAKDOWN_FIELD_MAP[key] || key;
      // Map to shorter labels for grid display
      const shortLabel = label
        .replace('Subscriptions', 'SUBSCRIPTIONS')
        .replace('Content Purchases', 'CONTENT PURCHASES')
        .replace('Tips', 'TIPS')
        .replace('Voice & Video Calls', 'VOICE & VIDEO')
        .replace('Message Purchases', 'MESSAGE PURCHASES')
        .replace('Message Tips', 'MESSAGE TIPS')
        .replace('Referrals', 'REFERRALS')
        .toUpperCase();
      return { label: shortLabel, value };
    });

  let summaryGridHTML = '';
  summaryItems.forEach(({ label, value }) => {
    summaryGridHTML += `
      <div class="summary-item">
        <span>${label}</span>
        <strong>$${formatUsdAmountDisplayCents(value)}</strong>
      </div>
    `;
  });

  // Generate Monthly Cards HTML
  let monthlyCardsHTML = '';
  monthlyData.forEach((month, monthIndex) => {
    const {
      monthName,
      year: monthYear,
      'daily-earnings': dailyEarnings = []
    } = month;
    const monthDisplay = `${monthName} ${monthYear}`;
    const dailyTableHTML = renderDailyEarningsTable(dailyEarnings, userRole);

    monthlyCardsHTML += `
      <div class="card" style="pading: 0px">
        <div class="table-title">
        <span>
            <svg class="calendar-days-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                <path d="M128 0c17.7 0 32 14.3 32 32V64H288V32c0-17.7 14.3-32 32-32s32 14.3 32 32V64h48c26.5 0 48 21.5 48 48v48H0V112C0 85.5 21.5 64 48 64H96V32c0-17.7 14.3-32 32-32zM0 192H448V464c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V192zm64 80v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H80c-8.8 0-16 7.2-16 16zm128 0v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H208c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H336zM64 400v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H80c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H208zm112 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H336c-8.8 0-16 7.2-16 16z"></path>
              </svg>
        </span>
        ${monthDisplay} 
        ${taxType === 'vat' ? `— VAT NUMBER ${taxNumber}` : ''}
        </div>
        ${dailyTableHTML}
      </div>
    `;
  });

  return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <title>Earnings Report PDF</title>
        <style>
          /* ===== PDF PAGE SETUP ===== */
          @page {
            size: A4;
            margin: 16mm;
          }

          * {
            box-sizing: border-box;
            font-family: Arial, Helvetica, sans-serif;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          body {
            margin: 0;
            background: #0f0f0f;
            color: #ffffff;
          }

          .container {
            width: 900px;
            margin: 0 auto;
          }

          /* ===== HEADER ===== */
          .header {
            display: flex;
            gap: 16px;
            align-items: center;
            background: rgba(30, 30, 30, 0.8);
            margin-bottom: 20px;
            padding: 24px 32px;
            border-bottom: 1px solid rgb(45, 45, 45);
          }

          .title {
            font-size: 20px;
            font-weight: bold;
          }

          .subtitle {
            font-size: 12px;
            color: #b5b5b5;
          }

          .download-btn {
            background: #ff2ea6;
            padding: 10px 14px;
            border-radius: 8px;
            font-size: 12px;
            font-weight: bold;
          }

          /* ===== CARD ===== */
          .card {
            background: #181818;
            border-radius: 14px;
            padding: 16px;
            margin-bottom: 18px;
            page-break-inside: avoid;
          }

          .row {
            display: flex;
            gap: 14px;
          }

          .info-box {
            flex: 1;
            padding: 12px;
            border-radius: 10px;
          }

          .label {
            font-size: 11px;
            color: #9a9a9a;
            margin-bottom: 4px;
          }

          .value {
            font-size: 14px;
            font-weight: bold;
          }

          /* ===== SUMMARY ===== */
          .summary-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
            gap: 8px;
            margin-top: 14px;
          }

          .summary-item {
            background: #1f1f1f;
            padding: 12px 6px;
            border-radius: 10px;
            text-align: center;
          }

          .summary-item span {
            font-size: 10px;
            color: #ff4fbf;
          }

          .summary-item strong {
            display: block;
            margin-top: 4px;
            font-size: 15px;
          }

          .vat-box {
            background: #3a2f12;
            padding: 12px;
            border-radius: 10px;
            margin-top: 16px;
            display: flex;
            justify-content: space-between;
            color: #ffcc33;
            font-weight: bold;
          }

          .total-earnings {
            margin-top: 12px;
            display: flex;
            padding-left: 12px;
            padding-right: 12px;
            justify-content: space-between;
            font-size: 16px;
            font-weight: bold;
          }

          .total-earnings span {
            color: #22c55e;
          }

          /* ===== TABLE ===== */
          .table-title {
            background: #ff2ea6;
            padding: 12px;
            font-weight: bold;
            border-radius: 10px 10px 0 0;
            page-break-after: avoid;
            display: flex;
            align-items: center;
            gap: 12px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            background: #181818;
            page-break-inside: auto;
          }

          th, td {
            padding: 10px;
            font-size: 11px;
            border-bottom: 1px solid #2b2b2b;
          }

          th {
            color: #bcbcbc;
            font-weight: normal;
            text-align: left;
          }

          tr {
            page-break-inside: avoid;
          }

          .earning-icon {
              border-radius: 8px;
              width: 47px;
              height: 52px;
              background: rgb(245, 52, 191);
              display: flex;
              align-items: center;
              padding: 12px;
          }

          .earning-icon svg {
              width: 22px;
              height: 22px;
              fill: #ffffff;
          }

          .info-item {
              display: flex;
              align-items: center;
              gap: 16px;
          }

          .info-item svg {
              width: 16px;
              height: 16px;
              color: rgb(245, 52, 191);
          }

          .info-item svg path {
              fill: rgb(245, 52, 191);
          }

          .summary-header {
                display: flex;
                align-items: center;
                gap: 16px;
                margin-bottom: 32px;
            }

            .earning-icon {
                  border-radius: 8px;
                  width: 47px;
                  height: 52px;
                  background: rgb(245, 52, 191);
                  display: flex;
                  align-items: center;
                  padding: 12px;
              }

              .earning-icon svg {
                  width: 22px;
                  height: 22px;
                  fill: #ffffff;
              }


              svg.calendar-days-icon {
                  width: 17px;
                  height: 20px;
              }

              svg.calendar-days-icon path {
                    fill: #fff;
                }


                footer {
                    width: 100%;
                    background: rgba(30, 30, 30, 0.8);
                    padding: 24px 32px;
                    border-top: 1px solid rgb(45, 45, 45);
                }

                footer .container {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }


                footer p {
                      margin: 0;
                      font-size: 14px;
                      font-weight: 500;
                      align-items: center;
                      display: flex;
                      gap: 8px;
                  }

                  footer span {
                      color: rgb(160 160 160 / 16%);
                  }


                  p.confidential {
                        background: rgb(30, 30, 30);
                        margin-left: auto;
                        border: 1px solid rgb(45, 45, 45);
                        padding: 8px 16px;
                        border-radius: 8px;
                        color: rgb(239, 68, 68);
                    }


                    footer svg {
                        height: 16px;
                        width: auto;
                    }

                    footer svg path {
                        fill: rgb(245, 52, 191);
                    }


                    footer p.confidential svg path {
                        fill: rgb(239, 68, 68);
                    }

        
        </style>
      </head>
      <body>
        <div class="container">
          
          <!-- HEADER -->
          <div class="header">
            <div class="earning-icon">
              <svg class="export-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
              <path d="M0 64C0 28.7 28.7 0 64 0H224V128c0 17.7 14.3 32 32 32H384V288H216c-13.3 0-24 10.7-24 24s10.7 24 24 24H384V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V64zM384 336V288H494.1l-39-39c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l80 80c9.4 9.4 9.4 24.6 0 33.9l-80 80c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l39-39H384zm0-208H256V0L384 128z"></path>
              </svg>
          </div>
            <div>
              <div class="title">Earnings Report</div>
              <div class="subtitle">Comprehensive earnings report</div>
            </div>

          </div>
          </div>
          <div style="padding: 0 20px;">
          <div class="container" style="width: 860px;">

          <!-- INFO -->
          <div class="card">
          
            <div class="row">
              <div class="info-box">
                  <div class="info-item">
                      <svg class="calendar-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                          <path d="M96 32V64H48C21.5 64 0 85.5 0 112v48H448V112c0-26.5-21.5-48-48-48H352V32c0-17.7-14.3-32-32-32s-32 14.3-32 32V64H160V32c0-17.7-14.3-32-32-32S96 14.3 96 32zM448 192H0V464c0 26.5 21.5 48 48 48H400c26.5 0 48-21.5 48-48V192z"></path>
                      </svg>
                      <div>
                          <div class="label">GENERATED</div>
                          <div class="value">${generatedDate}</div>
                      </div>
                  </div>
                  
              </div>
              <div class="info-box">

                  <div class="info-item">
                      <svg class="clock-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                          <path d="M256 0a256 256 0 1 1 0 512A256 256 0 1 1 256 0zM232 120V256c0 8 4 15.5 10.7 20l96 64c11 7.4 25.9 4.4 33.3-6.7s4.4-25.9-6.7-33.3L280 243.2V120c0-13.3-10.7-24-24-24s-24 10.7-24 24z"></path>
                        </svg>
                      <div>
                          <div class="label">PERIOD</div>
                          <div class="value">${period}</div>
                      </div>
                  </div>


                
              </div>
              </div>
          </div>

          <!-- SUMMARY -->
          <div class="card">
            <div class="summary-header">
              <div class="earning-icon">
                  <svg class="chart-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                      <path d="M32 32c17.7 0 32 14.3 32 32V400c0 8.8 7.2 16 16 16H480c17.7 0 32 14.3 32 32s-14.3 32-32 32H80c-44.2 0-80-35.8-80-80V64C0 46.3 14.3 32 32 32zm96 96c0-17.7 14.3-32 32-32l192 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-192 0c-17.7 0-32-14.3-32-32zm32 64H288c17.7 0 32 14.3 32 32s-14.3 32-32 32H160c-17.7 0-32-14.3-32-32s14.3-32 32-32zm0 96H416c17.7 0 32 14.3 32 32s-14.3 32-32 32H160c-17.7 0-32-14.3-32-32s14.3-32 32-32z"></path>
                    </svg>
              </div>
              <div>
                  <div class="title">Summary Overview</div>
              </div>
          </div>

          ${taxType === 'vat' ? `
            <div class="label">
              VAT NUMBER ${taxNumber}
            </div>
            ` : ''}
            ${taxType === 'gst' ? `
            <div class="label">
              GST NUMBER ${taxNumber}
            </div>
            ` : ''}

            <div class="summary-grid">
              ${summaryGridHTML}
            </div>

            ${taxType === 'vat' ? `
            <div class="vat-box">
              <div>Total VAT Remitted</div>
              <div>$${formatUsdAmountDisplayCents(totalVatRemitted)}</div>
            </div>
            ` : ''}
           
            <div class="total-earnings">
              <div>Total Earnings</div>
              <span>$${formatUsdAmountDisplayCents(earningsData.totalEarning || 0)}</span>
            </div>
          </div>

          <!-- MONTHLY TABLES -->
          ${monthlyCardsHTML}
        </div>
        </div>

        <footer>
          <div class="container">
              <p><svg class="calendar-check-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                  <path d="M128 0c17.7 0 32 14.3 32 32V64H288V32c0-17.7 14.3-32 32-32s32 14.3 32 32V64h48c26.5 0 48 21.5 48 48v48H0V112C0 85.5 21.5 64 48 64H96V32c0-17.7 14.3-32 32-32zM0 192H448V464c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V192zM329 305c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-95 95-47-47c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l64 64c9.4 9.4 24.6 9.4 33.9 0L329 305z"></path>
                </svg>
                  Generated on ${generatedDate}</p>
              <span>|</span>
              <p>
                  <svg class="fingerprint-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                      <path d="M48 256C48 141.1 141.1 48 256 48c63.1 0 119.6 28.1 157.8 72.5c8.6 10.1 23.8 11.2 33.8 2.6s11.2-23.8 2.6-33.8C403.3 34.6 333.7 0 256 0C114.6 0 0 114.6 0 256v40c0 13.3 10.7 24 24 24s24-10.7 24-24V256zm458.5-52.9c-2.7-13-15.5-21.3-28.4-18.5s-21.3 15.5-18.5 28.4c2.9 13.9 4.5 28.3 4.5 43.1v40c0 13.3 10.7 24 24 24s24-10.7 24-24V256c0-18.1-1.9-35.8-5.5-52.9zM256 80c-19 0-37.4 3-54.5 8.6c-15.2 5-18.7 23.7-8.3 35.9c7.1 8.3 18.8 10.8 29.4 7.9c10.6-2.9 21.8-4.4 33.4-4.4c70.7 0 128 57.3 128 128v24.9c0 25.2-1.5 50.3-4.4 75.3c-1.7 14.6 9.4 27.8 24.2 27.8c11.8 0 21.9-8.6 23.3-20.3c3.3-27.4 5-55 5-82.7V256c0-97.2-78.8-176-176-176zM150.7 148.7c-9.1-10.6-25.3-11.4-33.9-.4C93.7 178 80 215.4 80 256v24.9c0 24.2-2.6 48.4-7.8 71.9C68.8 368.4 80.1 384 96.1 384c10.5 0 19.9-7 22.2-17.3c6.4-28.1 9.7-56.8 9.7-85.8V256c0-27.2 8.5-52.4 22.9-73.1c7.2-10.4 8-24.6-.2-34.2zM256 160c-53 0-96 43-96 96v24.9c0 35.9-4.6 71.5-13.8 106.1c-3.8 14.3 6.7 29 21.5 29c9.5 0 17.9-6.2 20.4-15.4c10.5-39 15.9-79.2 15.9-119.7V256c0-28.7 23.3-52 52-52s52 23.3 52 52v24.9c0 36.3-3.5 72.4-10.4 107.9c-2.7 13.9 7.7 27.2 21.8 27.2c10.2 0 19-7 21-17c7.7-38.8 11.6-78.3 11.6-118.1V256c0-53-43-96-96-96zm24 96c0-13.3-10.7-24-24-24s-24 10.7-24 24v24.9c0 59.9-11 119.3-32.5 175.2l-5.9 15.3c-4.8 12.4 1.4 26.3 13.8 31s26.3-1.4 31-13.8l5.9-15.3C267.9 411.9 280 346.7 280 280.9V256z"></path>
                    </svg>
                    Document ID: ${documentId}</p>
              <p class="confidential">
                  <svg class="shield-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                      <path d="M256 0c4.6 0 9.2 1 13.4 2.9L457.7 82.8c22 9.3 38.4 31 38.3 57.2c-.5 99.2-41.3 280.7-213.6 363.2c-16.7 8-36.1 8-52.8 0C57.3 420.7 16.5 239.2 16 140c-.1-26.2 16.3-47.9 38.3-57.2L242.7 2.9C246.8 1 251.4 0 256 0zm0 66.8V444.8C394 378 431.1 230.1 432 141.4L256 66.8l0 0z"></path>
                    </svg>
                    Confidential Document</p>
          </div>
        </footer>
      </body>
    </html>
  `;
};